/**
 * A Beyblade: a Matter physics body + spin/burst state + the logic that turns a
 * Command into movement (steer, dash, extreme dash, hold center). Visuals are a
 * sharp "face" sprite (rotated to show spin) plus a "blur" disc that fades in at
 * high speed (ASSETS.md spinning-top rendering plan).
 */
import Phaser from 'phaser';
import { artBase, type BeyDef } from '../game/stats.ts';
import { idleDrainPerSecond, decayBurst } from '../game/combat.ts';
import { specialFor, isSpecialReady, type SpecialKind } from '../game/specials.ts';
import { SIM } from '../game/config.ts';
import type { Command, BeySnapshot } from '../game/command.ts';
import type { Vec2 } from '../game/vec.ts';
import { sub, len, normalize } from '../game/vec.ts';
import { WORLD, PHYS } from '../world.ts';

type ExtremePhase = 'none' | 'out' | 'rail' | 'in';

const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));

export class Bey {
  readonly def: BeyDef;
  readonly sprite: Phaser.Physics.Matter.Sprite;
  private readonly blur: Phaser.GameObjects.Sprite;
  private warnGlow!: Phaser.GameObjects.Sprite;
  private warnT = 0;
  private readonly center: Vec2;

  spin: number;
  burst = 0;
  alive = true;

  private dashCD = 0;
  private extremeCD = 0;
  private extremePhase: ExtremePhase = 'none';
  private extremeOutTime = 0;
  private railT = 0;
  private boostT = 0;
  private dashBoostT = 0;
  private knockedT = 0;
  private spinAngle = 0;

  /** Special-move charge meter (0..METER_MAX) and active-effect timers. */
  special = 0;
  private rushT = 0;
  private counterT = 0;
  private drainImmuneT = 0;
  private focusT = 0;

  /** Set true on the frame a dash / extreme dash fires, for the scene's FX. */
  firedDash = false;
  firedExtreme = false;
  /** Set to the special kind on the frame it fires (else null), for FX. */
  firedSpecial: SpecialKind | null = null;
  /** True while the bey is settled at center stage (sticky, no key needed). */
  atCenterStage = false;

  /** Arena physics for this bey (rim + center-pull), set from the profile. */
  private arenaRim: number = WORLD.arena.rim;
  private arenaPull: number = PHYS.pull;

  constructor(scene: Phaser.Scene, def: BeyDef, x: number, y: number, arena?: { rim: number; pull: number }) {
    this.def = def;
    this.spin = SIM.MAX_SPIN;
    this.center = { x: WORLD.arena.cx, y: WORLD.arena.cy };
    if (arena) {
      this.arenaRim = arena.rim;
      this.arenaPull = arena.pull;
    }

    // Pulsing danger glow shown as the burst meter climbs (telegraphs a burst).
    this.warnGlow = scene.add
      .sprite(x, y, 'glow')
      .setTint(0xff3020)
      .setBlendMode(Phaser.BlendModes.ADD)
      .setDepth(9)
      .setAlpha(0);
    this.warnGlow.setDisplaySize(WORLD.beyRadius * 5, WORLD.beyRadius * 5);

    const art = artBase(def);
    this.blur = scene.add.sprite(x, y, `bey-${art}-blur`);
    this.sprite = scene.matter.add.sprite(x, y, `bey-${art}-face`);

    // Scale the (1024px) art down to its display size, and size the *physics*
    // body to match the visible metal ring so beys bounce the moment their
    // edges touch (no overlap).
    // Set the sprite's *display* scale first — setScale also scales the matter
    // body, so we apply it BEFORE setCircle so the body lands at the intended
    // world-unit radius (otherwise the collision body got shrunk to ~1.9px and
    // beys could overlap massively before colliding).
    const target = WORLD.beyRadius * 2.5; // display diameter
    this.baseScale = target / this.sprite.width;
    this.sprite.setScale(this.baseScale).setDepth(10);
    this.blur.setScale((target * 1.04) / this.blur.width).setAlpha(0).setDepth(11);

    const ringRadius = (target / 2) * WORLD.ringFill;
    this.sprite.setCircle(ringRadius);
    this.sprite.setFriction(0, 0, 0);
    this.sprite.setFrictionAir(0);
    // High restitution + tight slop = beys bounce immediately on edge contact
    // instead of interpenetrating before Matter resolves them.
    this.sprite.setBounce(0.85);
    (this.sprite.body as MatterJS.BodyType).slop = 0.01;
    this.sprite.setMass(0.5 + def.stats.weight * 0.12);
    this.sprite.setData('bey', this);
  }

  /** Resting sprite scale (used by the scene to pop the bey during a charge). */
  readonly baseScale: number;

  /** Restore the bey to a fresh full-spin state at (x, y) for a new round. */
  reset(x: number, y: number): void {
    this.spin = SIM.MAX_SPIN;
    this.burst = 0;
    this.alive = true;
    this.dashCD = 0;
    this.extremeCD = 0;
    this.extremePhase = 'none';
    this.extremeOutTime = 0;
    this.railT = 0;
    this.boostT = 0;
    this.dashBoostT = 0;
    this.knockedT = 0;
    this.special = 0;
    this.rushT = 0;
    this.counterT = 0;
    this.drainImmuneT = 0;
    this.focusT = 0;
    this.firedDash = false;
    this.firedExtreme = false;
    this.firedSpecial = null;
    this.atCenterStage = false;
    this.sprite.setPosition(x, y);
    this.sprite.setVelocity(0, 0);
    this.sprite.setScale(this.baseScale).setVisible(true).setAlpha(1);
    this.blur.setPosition(x, y).setAlpha(0);
    this.warnGlow.setPosition(x, y).setAlpha(0);
  }

  get pos(): Vec2 {
    return { x: this.sprite.x, y: this.sprite.y };
  }

  get velocity(): Vec2 {
    const b = this.sprite.body as MatterJS.BodyType;
    return { x: b.velocity.x, y: b.velocity.y };
  }

  /** Distance from arena center. */
  private distFromCenter(): number {
    return len(sub(this.pos, this.center));
  }

  /** Extra impact multiplier while an extreme-dash or rush window is active. */
  get impactMultiplier(): number {
    if (this.rushT > 0) return PHYS.special.rushImpactBonus;
    return this.boostT > 0 ? PHYS.extreme.impactBonus : 1;
  }

  /** True while Iron Counter is up — the next hit reflects (see scene). */
  get counterActive(): boolean {
    return this.counterT > 0;
  }

  /** True while the special meter is full and ready to fire. */
  get specialReady(): boolean {
    return isSpecialReady(this.special);
  }

  snapshot(): BeySnapshot {
    return {
      type: this.def.type,
      pos: this.pos,
      vel: this.velocity,
      spin: this.spin,
      maxSpin: SIM.MAX_SPIN,
      burst: this.burst,
      burstMax: SIM.BURST.METER_MAX,
      dashCooldown: this.dashCD,
      extremeCooldown: this.extremeCD,
      special: this.special,
      specialMax: SIM.SPECIAL.METER_MAX,
    };
  }

  /**
   * Kick the bey into a tangential orbit around the dish center. `spin` is +1
   * for counter-clockwise, -1 for clockwise — opposing signs make the two beys
   * cross paths and clash periodically.
   */
  launchOrbit(spin: 1 | -1, speedMult = 1): void {
    const radial = normalize(sub(this.pos, this.center));
    // Perpendicular to the radius = tangent to the orbit.
    const tangent = { x: -radial.y * spin, y: radial.x * spin };
    const v = PHYS.launchSpeed * speedMult;
    this.sprite.setVelocity(tangent.x * v, tangent.y * v);
  }

  /** Apply a clash result: lose spin, gain burst meter. */
  applyClash(spinLoss: number, burstGain: number): void {
    this.spin = Math.max(0, this.spin - spinLoss);
    this.burst = clamp(this.burst + burstGain, 0, SIM.BURST.METER_MAX);
  }

  /** Charge the special meter from a clash (spin dealt to / taken from foe). */
  chargeSpecial(dealt: number, taken: number): void {
    const s = SIM.SPECIAL;
    this.special = Math.min(s.METER_MAX, this.special + dealt * s.GAIN_PER_DEALT + taken * s.GAIN_PER_TAKEN);
  }

  /** Spend Iron Counter after it reflects a hit. */
  consumeCounter(): void {
    this.counterT = 0;
  }

  /**
   * Fire the bey's special (assumes the meter is full). Applies the archetype
   * effect, resets the meter, and records the kind for the scene's FX.
   */
  private activateSpecial(opponentPos: Vec2): void {
    const def = specialFor(this.def.type);
    this.special = 0;
    this.firedSpecial = def.kind;
    switch (def.kind) {
      case 'rush': {
        this.rushT = def.duration;
        const dir = normalize(sub(opponentPos, this.pos));
        this.sprite.setVelocity(dir.x * PHYS.special.rushSpeed, dir.y * PHYS.special.rushSpeed);
        break;
      }
      case 'counter':
        this.counterT = def.duration;
        break;
      case 'secondWind':
        this.spin = Math.min(SIM.MAX_SPIN, this.spin + SIM.SPECIAL.SECOND_WIND_HEAL);
        this.drainImmuneT = def.duration;
        break;
      case 'focus':
        this.focusT = def.duration;
        this.spin = Math.min(SIM.MAX_SPIN, this.spin + PHYS.special.focusHeal);
        break;
    }
  }

  /** Flag that the bey was just knocked by a clash it lost (ring-out window). */
  markKnocked(): void {
    this.knockedT = PHYS.ringOut.hitWindow;
  }

  /** True while a recent knock makes the bey eligible to be rung out. */
  get canRingOut(): boolean {
    return this.knockedT > 0;
  }

  /** Add a knockback impulse (engine velocity units). */
  addImpulse(v: Vec2): void {
    const cur = this.velocity;
    this.sprite.setVelocity(cur.x + v.x, cur.y + v.y);
  }

  /**
   * Per-physics-step movement update. Runs in the matter `beforeupdate` hook so
   * forces are integrated this step.
   */
  preStep(dt: number, cmd: Command, opponentPos: Vec2): void {
    const dtScale = dt * 60;
    this.firedDash = false;
    this.firedExtreme = false;
    this.firedSpecial = null;
    this.dashCD = Math.max(0, this.dashCD - dt);
    this.extremeCD = Math.max(0, this.extremeCD - dt);
    this.boostT = Math.max(0, this.boostT - dt);
    this.dashBoostT = Math.max(0, this.dashBoostT - dt);
    this.knockedT = Math.max(0, this.knockedT - dt);
    this.rushT = Math.max(0, this.rushT - dt);
    this.counterT = Math.max(0, this.counterT - dt);
    this.drainImmuneT = Math.max(0, this.drainImmuneT - dt);
    this.focusT = Math.max(0, this.focusT - dt);
    if (this.boostT === 0 && this.extremePhase === 'in') this.extremePhase = 'none';

    // Charge the special meter passively, and fire it on command when full.
    if (this.special < SIM.SPECIAL.METER_MAX) {
      this.special = Math.min(SIM.SPECIAL.METER_MAX, this.special + SIM.SPECIAL.TRICKLE_PER_SEC * dt);
    }
    if (cmd.special && this.specialReady && this.extremePhase === 'none' && this.rushT <= 0) {
      this.activateSpecial(opponentPos);
    }

    const v = this.velocity;
    const pos = this.pos;
    const speed = len(v);
    const spinFactor = clamp(this.spin / SIM.MAX_SPIN, 0.25, 1);
    const steering = len(cmd.steer) > 0.01;

    // Blazing Rush: a homing super-dash. Override movement to track the foe.
    if (this.rushT > 0) {
      const dir = normalize(sub(opponentPos, pos));
      this.sprite.setVelocity(dir.x * PHYS.special.rushSpeed, dir.y * PHYS.special.rushSpeed);
      return;
    }

    let pullMult = 1;
    let drag: number = PHYS.drag;
    let steerAuthority = spinFactor;

    // Center stage is sticky: holding the key claims it, but a bey that simply
    // drifts to the middle and slows down stays anchored there on its own —
    // until the player steers away into a larger arc.
    const driftedIn =
      this.distFromCenter() < this.arenaRim * 0.2 && speed < PHYS.centerStageSpeed && !steering;
    this.atCenterStage = (cmd.holdCenter || driftedIn) && this.extremePhase === 'none';
    if (this.atCenterStage) {
      pullMult = PHYS.holdPullMult;
      drag = PHYS.holdDrag;
      steerAuthority *= 0.5;
    }
    // Tailwind (balance special): more steering authority while active.
    if (this.focusT > 0) steerAuthority *= PHYS.special.focusSteerMult;

    // Center-pull (the bowl).
    const toCenter = sub(this.center, pos);
    v.x += toCenter.x * this.arenaPull * pullMult * dtScale;
    v.y += toCenter.y * this.arenaPull * pullMult * dtScale;

    if (this.extremePhase === 'out') {
      this.extremeOutTime += dt;
      const outward = this.distFromCenter() < 1 ? normalize(sub(opponentPos, pos)) : normalize(sub(pos, this.center));
      v.x += outward.x * PHYS.extreme.outForce * dtScale;
      v.y += outward.y * PHYS.extreme.outForce * dtScale;
      // Reached the rail -> ride around the rim before striking.
      if (this.distFromCenter() >= this.arenaRim * 0.9 || this.extremeOutTime > 1.1) {
        this.extremePhase = 'rail';
        this.railT = PHYS.extreme.railTime;
      }
    } else if (this.extremePhase === 'rail') {
      // Carve along the rim (tangentially, preserving angular direction) for a
      // beat, then slingshot toward the opponent for the strike.
      this.railT -= dt;
      const radial = normalize(sub(pos, this.center));
      const tangent = { x: -radial.y, y: radial.x };
      const sign = v.x * tangent.x + v.y * tangent.y >= 0 ? 1 : -1;
      const rs = PHYS.extreme.railSpeed;
      v.x = tangent.x * sign * rs;
      v.y = tangent.y * sign * rs;
      // Gently hold the rim radius.
      const corr = (this.distFromCenter() - this.arenaRim * 0.95) * 0.25;
      v.x -= radial.x * corr;
      v.y -= radial.y * corr;
      if (this.railT <= 0) {
        const dir = normalize(sub(opponentPos, pos));
        v.x = dir.x * PHYS.extreme.slingSpeed;
        v.y = dir.y * PHYS.extreme.slingSpeed;
        this.extremePhase = 'in';
        this.boostT = PHYS.extreme.boostTime;
      }
    } else {
      // Normal steering.
      if (len(cmd.steer) > 0.01) {
        const a = PHYS.steerAccel * (this.def.stats.speed / 10) * steerAuthority * dtScale;
        v.x += cmd.steer.x * a;
        v.y += cmd.steer.y * a;
      }
      // Strike / dash — a big committed lunge that overshoots if it misses.
      if (cmd.dash && this.dashCD <= 0) {
        const dir = steering ? normalize(cmd.steer) : normalize(sub(opponentPos, pos));
        v.x += dir.x * PHYS.dash.impulse;
        v.y += dir.y * PHYS.dash.impulse;
        this.dashCD = PHYS.dash.cooldown;
        this.dashBoostT = PHYS.dash.boostTime;
        this.spin = Math.max(0, this.spin - PHYS.dash.spinCost);
        this.firedDash = true;
      }
      // Extreme dash (begin the rail ride).
      if (cmd.extremeDash && this.extremeCD <= 0 && this.spin > SIM.MAX_SPIN * 0.2) {
        this.extremePhase = 'out';
        this.extremeOutTime = 0;
        this.extremeCD = PHYS.extreme.cooldown;
        this.spin = Math.max(0, this.spin - PHYS.extreme.spinCost);
        this.firedExtreme = true;
      }
    }

    // Drag.
    const dragFactor = Math.pow(drag, dtScale);
    v.x *= dragFactor;
    v.y *= dragFactor;

    // Speed cap — relaxed during an extreme dash (so the slingshot lands) and
    // briefly after a strike (so the lunge actually flings the bey).
    let cap: number = PHYS.maxSpeed;
    if (this.extremePhase !== 'none') cap = PHYS.extreme.slingSpeed + 2;
    else if (this.dashBoostT > 0) cap = PHYS.dash.boostCap;
    const sp = len(v);
    if (sp > cap) {
      const k = cap / sp;
      v.x *= k;
      v.y *= k;
    }

    this.sprite.setVelocity(v.x, v.y);

    // Spin drain. Second Wind suspends it entirely; center stage and Tailwind
    // slow it.
    let drain = idleDrainPerSecond(this.def.stats.stamina) * dt;
    if (this.drainImmuneT > 0) drain = 0;
    else {
      if (this.atCenterStage) drain *= 0.7;
      if (this.focusT > 0) drain *= PHYS.special.focusDrainMult;
    }
    this.spin = Math.max(0, this.spin - drain);
    this.burst = decayBurst(this.burst, dt);
  }

  /** Update visuals + per-frame burst decay-independent cosmetics. */
  render(dt: number): void {
    const spinFactor = clamp(this.spin / SIM.MAX_SPIN, 0, 1);
    const speed = len(this.velocity);

    // Spin the face. Spin rate scales with remaining spin (a dying top slows).
    const dir = this.def.spinDir === 'left' ? -1 : 1;
    this.spinAngle += dir * (6 + spinFactor * 26) * dt;
    this.sprite.setRotation(this.spinAngle);

    // Wobble when nearly out of spin.
    let wob = 0;
    if (spinFactor < 0.28) {
      wob = (0.28 - spinFactor) * 6;
      this.sprite.x += Math.sin(this.spinAngle * 3) * wob * 0.4;
    }

    // Crossfade to the motion-blur sprite as the bey moves; counter-rotate it
    // for a smear feel and dim the sharp face underneath.
    this.blur.setPosition(this.sprite.x, this.sprite.y);
    this.blur.setRotation(-this.spinAngle * 0.6);
    const blurFactor = clamp((speed / PHYS.maxSpeed) * (0.45 + 0.55 * spinFactor), 0, 1);
    this.blur.setAlpha(0.85 * blurFactor);
    this.sprite.setAlpha(1 - 0.55 * blurFactor);

    // Burst danger telegraph: a red glow that pulses faster/brighter as the
    // burst meter climbs past ~55%, warning of an impending break-apart.
    this.warnT += dt;
    const burstRatio = this.burst / SIM.BURST.METER_MAX;
    if (burstRatio > 0.55) {
      const warn = (burstRatio - 0.55) / 0.45; // 0..1
      const pulse = 0.5 + 0.5 * Math.sin(this.warnT * (8 + warn * 10));
      this.warnGlow.setPosition(this.sprite.x, this.sprite.y);
      this.warnGlow.setAlpha(warn * (0.35 + 0.55 * pulse));
      const s = WORLD.beyRadius * (4.5 + warn * 1.5);
      this.warnGlow.setDisplaySize(s, s);
    } else {
      this.warnGlow.setAlpha(0);
    }
  }

  /** Glow tint for the extreme-dash boost window (used by the scene for FX). */
  get isCharging(): boolean {
    return this.extremePhase !== 'none';
  }

  destroy(): void {
    this.sprite.destroy();
    this.blur.destroy();
    this.warnGlow.destroy();
  }
}
